/**
 * Desktop camera behaviour (RTFocusCamera + DesktopCameraFocus + GallerySceneController chords):
 * free trackball orbit 0.5°/px, RMB pan, wheel / MMB zoom, LMB+RMB window-level chord, MMB+RMB
 * camera-clip chord, F focus, A/P/L/R/S/I snaps, orthographic toggle, smooth focus.
 * Touch: one finger orbits, two fingers pinch to zoom and drag to pan (no chords).
 * Works in three.js world units (= Unity units, 1 = 100 mm).
 */
import * as THREE from 'three';

export interface ControlSpeeds {
  rotate: number;
  pan: number;
  zoom: number;
}

export interface ChordHandlers {
  /** LMB+RMB drag: dx, dy in px since chord start. */
  onWindowLevel?: (dxPx: number, dyPx: number, phase: 'start' | 'move' | 'end') => void;
  /** MMB+RMB vertical drag. */
  onCameraClip?: (dyPx: number, phase: 'start' | 'move' | 'end') => void;
}

const FOV = 60;
const NEAR = 0.01;
const FAR = 1000;

export class CameraControls {
  readonly perspective: THREE.PerspectiveCamera;
  readonly orthographic: THREE.OrthographicCamera;
  camera: THREE.Camera;
  focus = new THREE.Vector3();
  speeds: ControlSpeeds = { rotate: 1, pan: 1, zoom: 1 };
  enabled = true;
  chords: ChordHandlers = {};
  onChange: (() => void) | null = null;
  onInteraction: ((active: boolean) => void) | null = null;

  private element: HTMLElement;
  private buttons = 0;
  private last = { x: 0, y: 0 };
  private chordStart: { x: number; y: number; kind: 'wl' | 'clip' } | null = null;
  private chordVeto = false;
  /** Active touch points; with two of them the gesture is pinch-zoom + pan, never orbit. */
  private touches = new Map<number, { x: number; y: number }>();
  private pinch: { dist: number; cx: number; cy: number } | null = null;
  private smoothTarget: { position: THREE.Vector3; focus: THREE.Vector3; t: number; duration: number } | null = null;
  private interactionTimer: number | null = null;

  constructor(element: HTMLElement) {
    this.element = element;
    this.perspective = new THREE.PerspectiveCamera(FOV, 1, NEAR, FAR);
    this.orthographic = new THREE.OrthographicCamera(-1, 1, 1, -1, NEAR, FAR);
    this.camera = this.perspective;
    this.perspective.position.set(0, 0, -5);
    element.addEventListener('pointerdown', this.onPointerDown);
    element.addEventListener('pointermove', this.onPointerMove);
    element.addEventListener('pointerup', this.onPointerUp);
    element.addEventListener('pointercancel', this.onPointerUp);
    element.addEventListener('wheel', this.onWheel, { passive: false });
    element.addEventListener('contextmenu', (e) => e.preventDefault());
    element.addEventListener('dblclick', (e) => e.preventDefault());
  }

  dispose(): void {
    this.element.removeEventListener('pointerdown', this.onPointerDown);
    this.element.removeEventListener('pointermove', this.onPointerMove);
    this.element.removeEventListener('pointerup', this.onPointerUp);
    this.element.removeEventListener('pointercancel', this.onPointerUp);
    this.element.removeEventListener('wheel', this.onWheel);
  }

  // ------------------------------------------------------------ geometry
  get isOrthographic(): boolean {
    return this.camera === this.orthographic;
  }

  get focusDistance(): number {
    return this.camera.position.distanceTo(this.focus);
  }

  setAspect(aspect: number): void {
    this.perspective.aspect = aspect;
    this.perspective.updateProjectionMatrix();
    this.syncOrtho();
  }

  private syncOrtho(): void {
    const halfH = this.focusDistance * Math.tan(THREE.MathUtils.degToRad(FOV / 2));
    const halfW = halfH * this.perspective.aspect;
    this.orthographic.left = -halfW;
    this.orthographic.right = halfW;
    this.orthographic.top = halfH;
    this.orthographic.bottom = -halfH;
    this.orthographic.updateProjectionMatrix();
  }

  setOrthographic(on: boolean): void {
    const from = this.camera;
    const to = on ? this.orthographic : this.perspective;
    if (from === to) return;
    to.position.copy(from.position);
    to.quaternion.copy(from.quaternion);
    to.up.copy(from.up);
    this.camera = to;
    this.syncOrtho();
    this.changed();
  }

  /** RTG CameraFocus: distance = |size|*0.5/tan(fov/2) + add, keeping the rotation. */
  focusOnBox(box: THREE.Box3, add = 1.2, smooth = false, duration = 1.5): void {
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const distance = (size.length() * 0.5) / Math.tan(THREE.MathUtils.degToRad(FOV / 2)) + add;
    const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(this.camera.quaternion);
    const position = center.clone().sub(forward.multiplyScalar(distance));
    if (smooth) {
      this.smoothTarget = { position, focus: center, t: 0, duration };
    } else {
      this.camera.position.copy(position);
      this.focus.copy(center);
      this.syncOrtho();
      this.changed();
    }
  }

  focusOnPoint(point: THREE.Vector3, boxSize = 0.3, add = 0.05, smooth = true): void {
    const half = boxSize / 2;
    const box = new THREE.Box3(point.clone().subScalar(half), point.clone().addScalar(half));
    this.focusOnBox(box, add, smooth);
  }

  /** Per-frame: advance a smooth focus. Returns true while animating. */
  update(dt: number): boolean {
    const s = this.smoothTarget;
    if (!s) return false;
    s.t = Math.min(1, s.t + dt / Math.max(0.01, s.duration));
    const k = 1 - Math.pow(1 - s.t, 3);
    const pos = this.camera.position;
    const toPos = s.position.clone().sub(pos).multiplyScalar(Math.min(1, dt * 6));
    pos.add(toPos);
    this.focus.add(s.focus.clone().sub(this.focus).multiplyScalar(Math.min(1, dt * 6)));
    if (s.t >= 1 || pos.distanceTo(s.position) < 1e-4) {
      pos.copy(s.position);
      this.focus.copy(s.focus);
      this.smoothTarget = null;
    }
    this.syncOrtho();
    this.changed();
    return this.smoothTarget !== null || k < 1;
  }

  /** SetPoseLookingAt(target, pos) then an exact rotation (commissural views). */
  setPose(position: THREE.Vector3, lookTarget: THREE.Vector3, up: THREE.Vector3): void {
    this.smoothTarget = null;
    this.camera.position.copy(position);
    this.camera.up.copy(up);
    this.camera.lookAt(lookTarget);
    this.focus.copy(lookTarget);
    this.syncOrtho();
    this.changed();
  }

  /** OrientationIndicator snap: look along -faceDir with the given up, same focus distance. */
  snapTo(faceDir: THREE.Vector3, upHint: THREE.Vector3, altUpHint: THREE.Vector3): void {
    const viewDir = faceDir.clone().normalize().negate();
    let hint = upHint.clone();
    if (hint.lengthSq() < 1e-8 || Math.abs(hint.dot(viewDir)) > 0.95) hint = altUpHint.clone();
    let up = hint.clone().projectOnPlane(viewDir);
    if (up.lengthSq() < 1e-8) up = new THREE.Vector3(0, 1, 0).projectOnPlane(viewDir);
    if (up.lengthSq() < 1e-8) up = new THREE.Vector3().crossVectors(viewDir, new THREE.Vector3(1, 0, 0));
    up.normalize();
    const dist = this.focusDistance;
    this.smoothTarget = null;
    this.camera.position.copy(this.focus).sub(viewDir.clone().multiplyScalar(dist));
    this.camera.up.copy(up);
    this.camera.lookAt(this.focus);
    this.syncOrtho();
    this.changed();
  }

  /** Roll the camera in the screen plane (compass ring drag). */
  roll(deltaDeg: number): void {
    const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(this.camera.quaternion);
    const q = new THREE.Quaternion().setFromAxisAngle(forward, THREE.MathUtils.degToRad(-deltaDeg));
    this.camera.quaternion.premultiply(q);
    this.camera.up.applyQuaternion(q);
    this.changed();
  }

  // ------------------------------------------------------------ input
  private zoomFactor(): number {
    const h = this.element.clientHeight || 1;
    if (this.isOrthographic) {
      const size = this.orthographic.top; // half height
      return (size * 2) / (0.071 * h);
    }
    return this.focusDistance / (0.046 * h * (90 / FOV));
  }

  private onPointerDown = (e: PointerEvent): void => {
    if (!this.enabled) return;
    if ((e.target as HTMLElement).closest?.('button, input, select, .quick-bar, .compass, .frame-bar')) return;
    this.element.setPointerCapture(e.pointerId);
    this.buttons = e.buttons;
    this.last = { x: e.clientX, y: e.clientY };
    this.smoothTarget = null;
    if (e.pointerType === 'touch') {
      this.touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
      this.pinch = this.touches.size === 2 ? this.pinchState() : null;
      this.setInteracting(true);
      return;
    }
    // Chords: LMB+RMB window/level, MMB+RMB clip.
    if ((e.buttons & 1) !== 0 && (e.buttons & 2) !== 0) this.startChord('wl', e);
    else if ((e.buttons & 4) !== 0 && (e.buttons & 2) !== 0) this.startChord('clip', e);
    this.setInteracting(true);
  };

  private pinchState(): { dist: number; cx: number; cy: number } | null {
    if (this.touches.size !== 2) return null;
    const [a, b] = [...this.touches.values()];
    return { dist: Math.hypot(b.x - a.x, b.y - a.y), cx: (a.x + b.x) / 2, cy: (a.y + b.y) / 2 };
  }

  /** Two fingers: the spread zooms (60 px ≈ one wheel notch), the midpoint pans. */
  private onTouchMove(e: PointerEvent): void {
    const t = this.touches.get(e.pointerId);
    if (!t) return;
    t.x = e.clientX;
    t.y = e.clientY;
    if (this.touches.size < 2) {
      if (this.pinch) return;
      const dx = e.clientX - this.last.x;
      const dy = e.clientY - this.last.y;
      this.last = { x: e.clientX, y: e.clientY };
      this.orbit(dx, dy);
      return;
    }
    const now = this.pinchState();
    if (!now || !this.pinch) {
      this.pinch = now;
      return;
    }
    this.zoomBy((now.dist - this.pinch.dist) / 60);
    this.pan(now.cx - this.pinch.cx, now.cy - this.pinch.cy);
    this.pinch = now;
  }

  private startChord(kind: 'wl' | 'clip', e: PointerEvent): void {
    if (this.chordStart) return;
    this.chordStart = { x: e.clientX, y: e.clientY, kind };
    this.chordVeto = true;
    if (kind === 'wl') this.chords.onWindowLevel?.(0, 0, 'start');
    else this.chords.onCameraClip?.(0, 'start');
  }

  private onPointerMove = (e: PointerEvent): void => {
    if (!this.enabled || this.buttons === 0) return;
    if (e.pointerType === 'touch') {
      this.onTouchMove(e);
      return;
    }
    const dx = e.clientX - this.last.x;
    const dy = e.clientY - this.last.y;
    this.last = { x: e.clientX, y: e.clientY };
    if (!this.chordStart) {
      if ((e.buttons & 1) !== 0 && (e.buttons & 2) !== 0) this.startChord('wl', e);
      else if ((e.buttons & 4) !== 0 && (e.buttons & 2) !== 0) this.startChord('clip', e);
    }
    if (this.chordStart) {
      const cx = e.clientX - this.chordStart.x;
      const cy = e.clientY - this.chordStart.y;
      if (this.chordStart.kind === 'wl') this.chords.onWindowLevel?.(cx, cy, 'move');
      else this.chords.onCameraClip?.(cy, 'move');
      return;
    }
    if (this.chordVeto) return;
    if ((e.buttons & 4) !== 0) {
      // MMB drag zoom: Zoom(CalculateScrollZoomAmount(dy * 0.00133))
      this.zoomBy(dy * 0.00133 * 120);
    } else if ((e.buttons & 2) !== 0) {
      this.pan(dx, dy);
    } else if ((e.buttons & 1) !== 0) {
      this.orbit(dx, dy);
    }
  };

  private onPointerUp = (e: PointerEvent): void => {
    this.buttons = e.buttons;
    if (this.element.hasPointerCapture(e.pointerId) && e.buttons === 0) this.element.releasePointerCapture(e.pointerId);
    if (e.pointerType === 'touch') {
      this.touches.delete(e.pointerId);
      if (this.touches.size === 0) {
        this.pinch = null;
        this.setInteracting(false);
      } else {
        // A finger lifted after a pinch: the remaining one must not orbit until it lifts too,
        // otherwise the view jumps by the distance between the two fingers.
        this.buttons = 1;
        this.pinch = this.pinch ?? { dist: 0, cx: 0, cy: 0 };
      }
      return;
    }
    if (this.chordStart && (e.buttons & (this.chordStart.kind === 'wl' ? 3 : 6)) !== (this.chordStart.kind === 'wl' ? 3 : 6)) {
      if (this.chordStart.kind === 'wl') this.chords.onWindowLevel?.(0, 0, 'end');
      else this.chords.onCameraClip?.(0, 'end');
      this.chordStart = null;
    }
    if (e.buttons === 0) {
      this.chordVeto = false;
      this.setInteracting(false);
    }
  };

  private onWheel = (e: WheelEvent): void => {
    if (!this.enabled) return;
    e.preventDefault();
    // One notch (deltaY ~ 100) ≈ 7.8 % of the focus distance at 1080 px (desktop: s = scroll/120).
    const notches = -e.deltaY / 100;
    this.zoomBy(notches);
    this.setInteracting(true);
    this.setInteracting(false, 250);
  };

  private orbit(dxPx: number, dyPx: number): void {
    // Free trackball: rotX = -dy*0.5, rotY = dx*0.5 (degrees per pixel), applied about the
    // camera's own up and right axes; the object follows the mouse.
    const rotX = -dyPx * (0.5 * this.speeds.rotate);
    const rotY = dxPx * (0.5 * this.speeds.rotate);
    const camUp = new THREE.Vector3(0, 1, 0).applyQuaternion(this.camera.quaternion);
    const camRight = new THREE.Vector3(1, 0, 0).applyQuaternion(this.camera.quaternion);
    const q = new THREE.Quaternion()
      .setFromAxisAngle(camUp, THREE.MathUtils.degToRad(-rotY))
      .multiply(new THREE.Quaternion().setFromAxisAngle(camRight, THREE.MathUtils.degToRad(rotX)));
    const rel = this.camera.position.clone().sub(this.focus).applyQuaternion(q);
    this.camera.position.copy(this.focus).add(rel);
    this.camera.quaternion.premultiply(q);
    this.camera.up.applyQuaternion(q);
    this.changed();
  }

  private pan(dxPx: number, dyPx: number): void {
    const zf = this.zoomFactor();
    const px = -dxPx * (0.25 * this.speeds.pan) * zf;
    const py = dyPx * (0.25 * this.speeds.pan) * zf;
    const right = new THREE.Vector3(1, 0, 0).applyQuaternion(this.camera.quaternion);
    const up = new THREE.Vector3(0, 1, 0).applyQuaternion(this.camera.quaternion);
    const delta = right.multiplyScalar(px).add(up.multiplyScalar(py));
    this.camera.position.add(delta);
    this.focus.add(delta);
    this.changed();
  }

  /** notches: positive zooms in. amount = s * 700 * zf with s = notches/120. */
  private zoomBy(notches: number): void {
    const zf = this.zoomFactor();
    let amount = (notches / 120) * (700 * this.speeds.zoom) * zf;
    const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(this.camera.quaternion);
    const dist = this.focusDistance;
    if (amount > dist - 0.02) amount = dist - 0.02; // never pass through the focus point
    this.camera.position.add(forward.multiplyScalar(amount));
    this.syncOrtho();
    this.changed();
  }

  private setInteracting(active: boolean, delayMs = 0): void {
    if (this.interactionTimer) {
      window.clearTimeout(this.interactionTimer);
      this.interactionTimer = null;
    }
    if (active) this.onInteraction?.(true);
    else if (delayMs > 0) this.interactionTimer = window.setTimeout(() => this.onInteraction?.(false), delayMs);
    else this.onInteraction?.(false);
  }

  private changed(): void {
    this.onChange?.();
  }
}
